/** 견적 편집·발송 폼 입력 스키마. 금액 열은 받지 않는다 — calcQuote 가 만든다. */

import { z } from "zod";

import { isUuid } from "../../lib/uuid";
import { VAT_MODES } from "../money/vat";
import { normalizeRate } from "./calc";
import { AREA_M2_MAX, AREA_PYEONG_MAX, areaPairOf, m2ToPyeong } from "./area-unit";
import { QUOTE_KINDS } from "./types";
import { MARGIN_BP_MAX, MARGIN_BP_MIN } from "../template/price-margin";
import { TRADE_GROUPS } from "../template/trades";

const text = (max = 200) => z.string().trim().max(max);

/**
 * 금액 한 칸의 상한 — 1,000억원.
 *
 * 상한이 없으면 `1e300` 같은 값도 `z.number().int()` 를 통과한다(`Number.isInteger`
 * 는 참이다). 그 값이 곱해지면 `Infinity`·`NaN` 이 되고, 「0보다 큰가」 검사를
 * 전부 지나 **총액이 `NaN` 인 견적서가 발송된다.** 단가 한 칸에 1,000억을 넘길
 * 일은 없으니 여기서 잘라 둔다.
 */
export const MONEY_MAX = 100_000_000_000;

/** 수량 한 칸의 상한. 평·개·m 단위라 100만이면 충분하다. */
export const QTY_MAX = 1_000_000;

const money = z
  .number()
  .int("금액은 정수여야 합니다")
  .nonnegative()
  .max(MONEY_MAX, "금액이 너무 큽니다 — 자릿수를 확인해 주세요");

/** 수량은 1.5 처럼 소수가 온다. 단가·금액만 정수다. */
const qty = z
  .number()
  .nonnegative()
  .finite()
  .max(QTY_MAX, "수량이 너무 큽니다 — 자릿수를 확인해 주세요");

export interface QuoteItemInputShape {
  scope: string;
  item: string;
  unit: string;
  qty: number;
  material_unit_price: number;
  labor_unit_price: number;
  // 원가 단가(부가세 빼고) · 미입력 null(스키마를 거치면 늘 채워진다).
  cost_unit_price?: number | null;
  // 넣을 때의 적용 마진(만분율) — 「판매가 직접 입력」 판정용 · 사내 전용 · 미입력 null.
  base_margin_bp?: number | null;
  note: string;
  /** 공간 코드. 안 정했으면 null. */
  space_code: string | null;
  /** 수량을 만든 식. 없으면 null(스키마를 거치면 늘 채워진다). */
  qty_expr?: string | null;
  children: QuoteItemInputShape[];
}

/** 입력 타입을 unknown 으로 둔다 — 재귀 + default 조합이라 입력·출력 모양이 다르다. */
export const quoteItemSchema: z.ZodType<
  QuoteItemInputShape,
  z.ZodTypeDef,
  unknown
> = z.lazy(() =>
  z.object({
    scope: text(100).default(""),
    item: text(300).min(1, "항목명을 입력하세요"),
    unit: text(20).default(""),
    qty: qty.default(0),
    material_unit_price: money.default(0),
    labor_unit_price: money.default(0),
    cost_unit_price: money.nullable().default(null),
    base_margin_bp: z.number().int().min(MARGIN_BP_MIN).max(MARGIN_BP_MAX).nullable().default(null),
    note: text(500).default(""),
    /** 조직 코드값이라 모양만 본다 — 아는 코드인지는 저장소가 목록으로 본다. */
    space_code: text(60).nullable().default(null),
    // 200 은 qty-expr 의 QTY_EXPR_MAX_LENGTH·0021 check 와 같다(순환 import 라 숫자로 둔다).
    qty_expr: text(200).nullable().default(null),
    children: z.array(quoteItemSchema).default([]),
  })
);

export const quoteTradeSchema = z.object({
  group: z.enum(TRADE_GROUPS),
  /**
   * 내장 15개로 좁히지 않는다 — 회사가 추가한 공종이 견적에 들어온다.
   * 「이 회사가 쓸 수 있는 코드인가」 는 저장소가 목록을 들고 있는 자리에서
   * 본다. 여기서는 모양만 본다(빈 문자열·과한 길이 거르기).
   */
  trade: text(60).min(1, "공종 코드가 비어 있습니다"),
  name: text(100).min(1, "공종명을 입력하세요"),
  sort_order: z.number().int().nonnegative(),
  condition_text: text(2000).default(""),
  items: z.array(quoteItemSchema).default([]),
});

/** 공사 기간 한 칸의 상한. 10년을 넘기면 입력 실수다. */
const MAX_DURATION_DAYS = 3650;

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "날짜 형식이 올바르지 않습니다");

/**
 * 공정 한 줄. 화면은 공정 id 를 들고 다니지 않는다 — 공종과 같은 규칙으로
 * **코드**를 받고 저장소가 그 회사의 공정 행을 찾아 붙인다.
 */
export const quotePhasePlanSchema = z.object({
  phase_code: text(60).min(1, "공정 코드가 비어 있습니다"),
  duration_days: z
    .number()
    .int("작업일수는 정수여야 합니다")
    .nonnegative()
    .max(MAX_DURATION_DAYS, "공사 기간이 너무 깁니다"),
  /** 초안을 아직 안 펼쳤으면 null 이다. */
  start_date: isoDate.nullable().default(null),
  end_date: isoDate.nullable().default(null),
  note: text(300).default(""),
  // 면적 초안의 근거(#175) — 손으로만 친 줄은 안 보낸다(null).
  standard_item_code: text(60).min(1).nullish(),
  quantity_m2: z.number().positive().max(99_999_999.99).nullish(),
  crew_size: z.number().int().min(1).max(1000).nullish(),
  draft_days: z.number().positive().max(MAX_DURATION_DAYS).nullish(),
});

/** 견적의 시공일정 한 벌. 일정을 안 쓰면 착공일이 null 이고 줄도 비어 있다. */
export const quoteScheduleSchema = z.object({
  expected_start_date: isoDate
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  work_saturday: z.boolean().default(false),
  work_sunday: z.boolean().default(false),
  work_holiday: z.boolean().default(false),
  /**
   * 초안을 펼칠 때 쓴 공휴일. null 이면 못 붙였다는 뜻이다.
   * 화면이 만들어 보내는 값이 아니라 **서버가 조회해 돌려준 값을 되돌려 받는** 칸이다.
   */
  holidays: z.array(isoDate).max(400).nullable().default(null),
  phases: z.array(quotePhasePlanSchema).default([]),
  // 초안을 계산한 공정 표준 판 — 견적에 아직 판이 없을 때만 굳는다(저장소가 지금 판을 이긴다).
  process_standard_version_id: z
    .string()
    .refine(isUuid, "공정 표준 판 id 가 올바르지 않습니다")
    .nullish(),
});

export const quoteHeaderSchema = z.object({
  title: text(200).min(1, "견적 제목을 입력하세요"),
  /**
   * 관리비율. **bp(만분율)로 눌러서 저장한다** — 계산이 bp 로 도는데 행에는
   * `0.12345` 가 그대로 남으면 화면의 「12.345%」 와 받은 금액이 어긋난다.
   */
  mgmt_fee_rate: z.number().min(0).max(1).transform(normalizeRate),
  /** null 이면 `gross mod 10000` 이 기본값으로 들어간다. */
  rounding_adjustment: money
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  conditions: z.array(text(1000)).default([]),
  extra_notice: text(2000).default(""),
  // 판매 단가의 부가세 포함/별도 · 안 주면 손대지 않는다(저장소가 기존 값·회사 기본을 쓴다).
  vat_mode: z.enum(VAT_MODES).optional(),
});

/** 평. `numeric(10,2)` 라 둘째 자리에서 끊는다 — 가견적·상세견적이 같이 쓴다. */
const areaPyeong = z
  .number()
  .positive("면적을 입력하세요")
  .finite()
  .max(AREA_PYEONG_MAX, "면적이 너무 큽니다 — 자릿수를 확인해 주세요")
  .transform((v) => Math.round(v * 100) / 100)
  .refine((v) => v > 0, "면적이 너무 작습니다 — 0.01평 이상 입력하세요");

// m²(#173 입력 원본) — 평과 같은 자리 규칙 · 상한은 평 100,000 의 m² · 평이 0 으로 끊기는 값(약 0.016m² 미만)은 막는다(DB area_m2>0·평과 짝).
const areaM2 = z
  .number()
  .positive("면적을 입력하세요")
  .finite()
  .max(AREA_M2_MAX, "면적이 너무 큽니다 — 자릿수를 확인해 주세요")
  .transform((v) => Math.round(v * 100) / 100)
  .refine((v) => m2ToPyeong(v) !== null, "면적이 너무 작습니다 — 0.02m² 이상 입력하세요");

/** 초안 저장 — 헤더 + 공종 + 항목을 통째로 덮어쓴다. */
export const quoteDraftSchema = quoteHeaderSchema.extend({
  trades: z.array(quoteTradeSchema).default([]),
  /** 수량식의 `평`. 안 주면(undefined) 손대지 않고 null 이면 지운다. */
  area_pyeong: areaPyeong.nullable().optional(),
  // 면적 원본(m²) — 주면 평은 이 값에서 다시 계산한다(areaPairOf) · undefined 는 손대지 않음.
  area_m2: areaM2.nullable().optional(),
  /**
   * 시공일정. **안 주면 손대지 않는다** — 일정 패널을 안 연 화면이 저장할 때
   * 이미 넣어 둔 일정을 빈 값으로 지워 버리면 안 된다.
   */
  schedule: quoteScheduleSchema.optional(),
});

/**
 * 가견적 입력. **3칸이 전부다**(제품 원칙(옛 5절)) — 면적 · 평당단가 · (제목).
 * 금액 칸은 받지 않는다. 두 칸을 곱한 값이 곧 총액이다.
 */
export const roughQuoteSchema = z.object({
  title: text(200).min(1, "견적 제목을 입력하세요"),
  /**
   * 평. 34.5 처럼 소수가 온다.
   *
   * **소수 둘째 자리에서 끊는다** — DB 열이 `numeric(10,2)` 라 그대로 두면
   * 저장소가 반올림해 버리고, 그러면 굳어 있는 `total` 과 「면적 × 평당단가」 가
   * 어긋난다(34.567 로 계산한 금액이 34.57 짜리 개산서에 실린다).
   */
  area_pyeong: areaPyeong.optional(),
  // 면적 원본(m²) — 주면 평은 이 값에서 계산한다 · 둘 중 하나는 있어야 한다.
  area_m2: areaM2.optional(),
  /** 평당 단가(원). */
  unit_price: money.refine((v) => v > 0, "평당 단가를 입력하세요"),
  conditions: z.array(text(1000)).default([]),
  extra_notice: text(2000).default(""),
}).superRefine((v, ctx) => {
  if (!areaPairOf(v.area_m2, v.area_pyeong).area_pyeong) {
    ctx.addIssue({ code: "custom", path: ["area_m2"], message: "면적을 입력하세요" });
  }
});

export const quoteCreateSchema = z.object({
  customer_id: text(50).min(1),
  /** 안 주면 상세견적이다. 가견적은 만드는 자리에서만 넘긴다. */
  kind: z.enum(QUOTE_KINDS).optional(),
  title: text(200).min(1, "견적 제목을 입력하세요"),
  /** 템플릿 id 를 주면 그 뼈대로, 없으면 빈 견적으로 만든다. */
  template_id: text(50)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null)),
  // 다른 요율 칸과 같은 자로 눌러 둔다. 지금 호출자는 이 값을 안 넘기지만,
  // 넘기는 순간 저장 요율과 표시 요율이 갈리는 자리다.
  mgmt_fee_rate: z
    .number()
    .min(0)
    .max(1)
    .optional()
    // 안 준 것은 안 준 대로 둔다 — `normalizeRate` 를 그냥 물리면 `undefined` 가
    // 숫자로 바뀌어 「요율을 지정하지 않음」 이 사라진다.
    .transform((v) => (v === undefined ? undefined : normalizeRate(v))),
  // 상세견적의 평수 기본값(같은 고객의 가견적에서 이어받는다). 안 주면 비운다.
  area_pyeong: areaPyeong.nullable().optional(),
  // 이어받은 면적 원본(m²) — 주면 평보다 우선한다.
  area_m2: areaM2.nullable().optional(),
});

export const quoteSendSchema = z.object({
  /** 고객 휴대폰 뒷 4자리. 열람 인증 값이다. */
  last4: z.string().regex(/^\d{4}$/, "숫자 4자리여야 합니다"),
  /** 만료 시각. 파싱 가능한 시각만 받는다 — 문자열 비교로 만료를 보던 버그를 여기서 먼저 막는다. */
  expires_at: z
    .string()
    .min(1)
    .refine((v) => Number.isFinite(Date.parse(v)), "시각 형식이 아닙니다"),
});

export const quoteDecisionSchema = z.object({
  decision: z.enum(["WON", "LOST"]),
  lost_reason: text(300)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null)),
});

export const quoteFilterSchema = z.object({
  status: z.enum(["DRAFT", "SENT", "NEGOTIATING", "WON", "LOST"]).optional(),
});

export type RoughQuoteInput = z.infer<typeof roughQuoteSchema>;
export type QuoteItemInput = z.infer<typeof quoteItemSchema>;
export type QuoteTradeInput = z.infer<typeof quoteTradeSchema>;
export type QuoteDraftInput = z.infer<typeof quoteDraftSchema>;
export type QuoteCreateInput = z.infer<typeof quoteCreateSchema>;
export type QuoteSendInput = z.infer<typeof quoteSendSchema>;
export type QuoteFilter = z.infer<typeof quoteFilterSchema>;
